import React from 'react';
import PropTypes from 'prop-types';
import {
  COST_TYPE,
  COST_TYPE_LABELS,
} from '../../../../../constants/costTypes';
import { IconPill, IconClockHour3 } from '@tabler/icons-react';
import InfoUnavailable from '../../../../../components/common/InfoUnavailable';

const desiredOrder = ['Pre-Deductible', 'Initial Coverage', 'Catastrophic'];
const daysSupplyOrder = ['30 days supply', '60 days supply', '90 days supply'];
function PlanDrugCosts({ medicarePlanInfo }) {
  const refactoredInformation = medicarePlanInfo?.hits?.hits[0]?._source;

  const transformCostData = (sourceData) => {
    const result = {};

    // Populate result with the desiredOrder keys, ensuring they are present
    desiredOrder.forEach((level) => {
      result[level] = []; // Initialize as an empty array
    });

    sourceData.forEach((item) => {
      const coverageLevel = item.CoverageDetails.CoverageLevel.Description;
      const daysSupply = item.CoverageDetails.DaysSupply.Description;

      if (!result[coverageLevel]) {
        result[coverageLevel] = [];
      }

      result[coverageLevel].push({
        DaysSupply: daysSupply,
        TierSpecialty: item.CoverageDetails.TierSpecialty,
        DeductibleApplies: item.CoverageDetails.DeductibleApplies,
        CostSharing: item.CostSharing,
      });
    });

    // Sort the groups within each coverage level by days supply
    Object.keys(result).forEach((key) => {
      result[key].sort((a, b) => {
        const indexA = daysSupplyOrder.indexOf(a.DaysSupply);
        const indexB = daysSupplyOrder.indexOf(b.DaysSupply);

        // If both items are in our ordering array, sort by that order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        // If only one is in our ordering array, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        // If neither is in our ordering array, maintain original order
        return 0;
      });
    });

    return result;
  };

  const beneficiaryCosts = transformCostData(
    refactoredInformation?.beneficiary_costs || [],
  );

  const formatCostDisplay = (costType, costAmount) => {
    const amount = parseFloat(costAmount || 0);

    switch (costType) {
      case COST_TYPE.NOT_OFFERED:
        return COST_TYPE_LABELS[COST_TYPE.NOT_OFFERED];
      case COST_TYPE.COPAY:
        return `$${amount.toFixed(2)}`;
      case COST_TYPE.COINSURANCE:
        return `${(amount * 100).toFixed(0)}%`;
      default:
        return 'Not specified';
    }
  };

  const formatKey = (key) => {
    return key.replace(/([A-Z])/g, ' $1'); // Add space before capital letters
  };

  return (
    <div
      className="tab-pane fade"
      id="pills-drug-costs-medicare"
      role="tabpanel"
      aria-labelledby="pills-drug-costs-tab-medicare"
      tabIndex={0}
    >
      <div>
        <h1 className="drug-name">Plan &amp; Drug Costs</h1>
        {/* <span>Tier: {drugTier}</span> */}
        <div className="accordion ps-accordion" id="accordionExample">
          {Object.entries(beneficiaryCosts)
            .sort(
              ([a], [b]) => desiredOrder.indexOf(a) - desiredOrder.indexOf(b),
            )
            .map(([coverageLevel, groups], index) => (
              <div key={coverageLevel} className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${index ? 'collapsed' : ''} text-capitalize`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#flush-collapse-${index}`}
                    aria-expanded={index ? 'true' : 'false'}
                    aria-controls={`flush-collapse-${index}`}
                  >
                    <IconPill size={18} className="me-2 my-2"></IconPill>
                    {coverageLevel}
                  </button>
                </h2>
                <div
                  id={`flush-collapse-${index}`}
                  className={`accordion-collapse collapse ${index ? '' : 'show'}`}
                >
                  <div className="accordion-body">
                    {groups.length ? (
                      groups.map((group, index) => (
                        <div
                          key={`${coverageLevel}-${index}`}
                          className="ps-table table-responsive mb-4"
                        >
                          {/* Days Supply Heading */}
                          <h4 className="days-supply-heading">
                            {/* <IconClockHour3
                              size={16}
                              className="me-2 text-muted"
                            ></IconClockHour3> */}
                            {group.DaysSupply}
                          </h4>
                          {/* Tier and Deductible Details */}
                          <div className="row col-12 mt-3 ">
                            <div className="col-xl-6 col-lg-6 col-sm-6 col-12 drug-info">
                              <label>Tier Specialty</label>
                              <p>{group.TierSpecialty}</p>
                            </div>
                            <div className="col-xl-6 col-lg-6 col-sm-6 col-12 drug-info">
                              <label>Deductible Applies</label>
                              <p>{group.DeductibleApplies}</p>
                            </div>
                          </div>
                          {/* Cost Sharing Table */}
                          <table className="table mb-3">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Min Amount</th>
                                <th>Max Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(group.CostSharing).map(
                                ([key, value]) => (
                                  <tr key={key}>
                                    <td>{formatKey(key)}</td>
                                    <td>
                                      {formatCostDisplay(
                                        value.Type,
                                        value.Amount,
                                      )}
                                    </td>
                                    <td>{`$${value.MinimumAmount}`}</td>
                                    <td>{`$${value.MaximumAmount}`}</td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      ))
                    ) : (
                      <InfoUnavailable />
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

PlanDrugCosts.propTypes = {
  medicarePlanInfo: PropTypes.object.isRequired,
  drugTier: PropTypes.string.isRequired,
};

export default PlanDrugCosts;
